export type RelationshipLinkMode =
  | 'all_relational_data'
  | 'meaningful_interactions_only'
  | 'line_of_management_only'
  | string

export type RelationshipNodeDisplayMode =
  | 'initials_team'
  | 'initials_cohort'
  | 'initials'
  | 'none'
  | 'blurred'
  | string

export type RelationshipLayoutMode =
  | 'network'
  | 'ego'
  | string

export interface RelationshipPerson {
  [key: string]: any
  email?: string
  emailKey?: string
  fullName?: string
  firstname?: string
  lastname?: string
  cuttingName?: string
  profileImage?: string
  teamId?: string
  teamName?: string
  teamColor?: string
  boss?: string
  powerRating?: number
  impactRating?: number
  impact_rating?: number
  healthBar?: number
  healthbar?: number
  who5?: number | null
  nps?: number | null
  enps?: number | null
  culture?: number | null
  toxic?: number | null
  total_count?: number
  strong_count?: number
  totalMembers?: Array<string | RelationshipPerson>
  strongMembers?: Array<string | RelationshipPerson>
  innerMembers?: Array<string | RelationshipPerson>
  outerMembers?: Array<string | RelationshipPerson>
  totalArrData?: RelationshipPerson[]
  strongArrData?: RelationshipPerson[]
  innerArrData?: RelationshipPerson[]
  outerArrData?: RelationshipPerson[]
}

export interface RelationshipGraphNode {
  id: string
  email: string
  emailKey: string
  label: string
  shortLabel: string
  teamName: string
  teamColor: string
  profileImage: string
  powerRating: number
  attributeValue: number | null
  attributeColor: string
  isScoped: boolean
  isFocused: boolean
  size: number
  x: number
  y: number
  person: RelationshipPerson
}

export interface RelationshipGraphEdge {
  id: string
  source: string
  target: string
  weight: number
  type: 'relationship' | 'management'
  direction: 'one_way' | 'two_way'
  isStrong: boolean
  isFocused: boolean
}

export interface RelationshipGraphModel {
  nodes: RelationshipGraphNode[]
  edges: RelationshipGraphEdge[]
  totalNodeCount: number
  totalEdgeCount: number
  strongestHiddenEdgeWeight: number | null
}

export interface BuildRelationshipGraphOptions {
  scopedRows: RelationshipPerson[]
  allRows?: RelationshipPerson[]
  linkMode?: RelationshipLinkMode
  selectedAttribute?: string
  focusedEmail?: string
  maxEdges?: number
  includeConnectedOutsideScope?: boolean
  layoutMode?: RelationshipLayoutMode
}

const DEFAULT_NODE_COLOR = '#747474'
const DEFAULT_MAX_EDGES = 1800
const STRONG_CONNECTION_WEIGHT = 0.86
const STANDARD_CONNECTION_WEIGHT = 0.44
const MANAGEMENT_CONNECTION_WEIGHT = 0.24

function normalizeLinkMode(value: RelationshipLinkMode | undefined | null): RelationshipLinkMode {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

  if (normalized === 'meaningful_interactions_only') return 'meaningful_interactions_only'
  if (normalized === 'line_of_management_only' || normalized === 'management_line_only') return 'line_of_management_only'
  return 'all_relational_data'
}

function normalizeLayoutMode(value: RelationshipLayoutMode | undefined | null): RelationshipLayoutMode {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

  return normalized === 'ego' || normalized === 'profile' || normalized === 'person' ? 'ego' : 'network'
}

function normalizeEmail(value: string | undefined | null): string {
  return String(value || '').replace(/\|\|/g, '.').trim()
}

function toEmailKey(value: string | undefined | null): string {
  return String(value || '').replace(/\./g, '||').trim()
}

function personEmail(person: RelationshipPerson | undefined | null): string {
  if (!person) return ''
  return normalizeEmail(person.email || person.emailKey || '')
}

function personEmailKey(person: RelationshipPerson | undefined | null): string {
  const email = personEmail(person)
  return person && person.emailKey ? person.emailKey : toEmailKey(email)
}

function personName(person: RelationshipPerson): string {
  if (person.fullName) return person.fullName
  const name = `${person.firstname || ''} ${person.lastname || ''}`.trim()
  return name || personEmail(person)
}

function initialsFor(person: RelationshipPerson): string {
  if (person.cuttingName) return person.cuttingName
  const first = (person.firstname || '').charAt(0)
  const last = (person.lastname || '').charAt(0)
  if (first || last) return `${first}${last}`.toUpperCase()
  return personName(person)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
}

function numberOrNull(value: any): number | null {
  if (value === undefined || value === null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function firstNumberOrNull(...values: any[]): number | null {
  for (let i = 0; i < values.length; i++) {
    const n = numberOrNull(values[i])
    if (n !== null) return n
  }

  return null
}

function normalizePercentLikeScore(value: number | null, options: { allowFivePointScale?: boolean } = {}): number | null {
  if (value === null) return null
  const absValue = Math.abs(value)
  if (absValue > 0 && absValue < 1) return Math.round(value * 100)
  if (options.allowFivePointScale && value > 1 && value <= 5) return Math.round((value / 5) * 100)
  return value
}

function normalizeNpsScore(value: number | null): number | null {
  if (value === null) return null
  if (value > -1 && value < 1 && value !== 0) return Math.round(value * 100)
  return value
}

export function getRelationshipAttributeValue(
  person: RelationshipPerson,
  selectedAttribute = 'happiness',
): number | null {
  switch (selectedAttribute) {
    case 'impact_rating':
      return normalizePercentLikeScore(firstNumberOrNull(person.impactRating, person.impact_rating))
    case 'happiness':
      return normalizePercentLikeScore(firstNumberOrNull(person.healthBar, person.healthbar, person.happiness))
    case 'well-being':
      return normalizePercentLikeScore(firstNumberOrNull(person.who5, person.computed_who5, person['WHO-5'], person.wellbeing, person.well_being))
    case 'NPS':
      return normalizeNpsScore(firstNumberOrNull(person.nps, person.NPS, person.nps_score, person.npsScore, person.net_promoter_score))
    case 'eNPS':
      return normalizeNpsScore(firstNumberOrNull(person.enps, person.eNPS, person.ENPS, person.e_nps, person.enps_score, person.enpsScore, person.employee_nps, person.employee_net_promoter_score))
    case 'culture_index':
      return normalizePercentLikeScore(firstNumberOrNull(person.culture, person.culture_index, person.cultureIndex, person.culture_score, person.cultureScore), { allowFivePointScale: true })
    case 'toxic_behaviors':
      return firstNumberOrNull(person.toxic, person.toxic_behaviors, person.toxicBehaviors, person.toxic_behaviour, person.toxicBehaviour, person.toxic_behavior, person.toxicBehavior, person.toxicity, person.toxic_score, person.toxicScore)
    default:
      return numberOrNull(person.powerRating)
  }
}

export function relationshipAttributeColor(value: number | null, selectedAttribute = 'happiness'): string {
  if (value === null) return '#9CA3AF'

  if (selectedAttribute === 'NPS' || selectedAttribute === 'eNPS') {
    const score = normalizeNpsScore(value)
    if (score === null) return '#9CA3AF'
    if (score >= 0 && score <= 10) {
      if (score <= 6) return '#BF3328'
      if (score <= 8) return '#FBC02D'
      return '#3B8D3E'
    }
    if (score < 0) return '#BF3328'
    if (score < 30) return '#FBC02D'
    return '#3B8D3E'
  }

  if (selectedAttribute === 'toxic_behaviors') {
    const score = normalizePercentLikeScore(value)
    if (score === null) return '#9CA3AF'
    if (score >= 0 && score <= 5) {
      if (score <= 2) return '#66BB6A'
      if (score <= 3) return '#FBC02D'
      return '#F44336'
    }
    if (score <= 33) return '#66BB6A'
    if (score <= 66) return '#FBC02D'
    return '#F44336'
  }

  const score = normalizePercentLikeScore(value, { allowFivePointScale: selectedAttribute === 'culture_index' })
  if (score === null) return '#9CA3AF'
  if (selectedAttribute === 'impact_rating' && score < 40) return '#F44336'
  if (selectedAttribute !== 'impact_rating' && score < 50) return '#F44336'
  if (score < 70) return '#FBC02D'
  return '#66BB6A'
}

function memberToEmail(value: string | RelationshipPerson): string {
  if (typeof value === 'string') return normalizeEmail(value)
  return personEmail(value)
}

function memberList(person: RelationshipPerson, keyField: keyof RelationshipPerson, dataField: keyof RelationshipPerson): string[] {
  const values = person[keyField]
  if (Array.isArray(values) && values.length > 0) {
    return (values as Array<string | RelationshipPerson>).map(memberToEmail).filter(Boolean)
  }

  const dataValues = person[dataField]
  if (Array.isArray(dataValues) && dataValues.length > 0) {
    return (dataValues as Array<string | RelationshipPerson>).map(memberToEmail).filter(Boolean)
  }

  return []
}

function undirectedEdgeId(source: string, target: string): string {
  return source < target ? `${source}__${target}` : `${target}__${source}`
}

function pushUniqueEdge(edgesById: Record<string, RelationshipGraphEdge>, edge: RelationshipGraphEdge) {
  const existing = edgesById[edge.id]
  if (!existing) {
    edgesById[edge.id] = edge
    return
  }

  const isReciprocal = existing.type === edge.type && existing.source === edge.target && existing.target === edge.source
  const direction = existing.direction === 'two_way' || edge.direction === 'two_way' || isReciprocal ? 'two_way' : 'one_way'
  const shouldReplace = edge.weight > existing.weight || edge.isFocused
  const baseEdge = shouldReplace ? edge : existing

  edgesById[edge.id] = {
    ...baseEdge,
    direction,
    isFocused: existing.isFocused || edge.isFocused,
  }
}

function buildPersonIndexes(rows: RelationshipPerson[]) {
  const byEmail: Record<string, RelationshipPerson> = {}
  const byKey: Record<string, RelationshipPerson> = {}

  rows.forEach(row => {
    const email = personEmail(row)
    if (!email) return
    byEmail[email] = row
    byKey[personEmailKey(row)] = row
  })

  return { byEmail, byKey }
}

function makeNode(
  person: RelationshipPerson,
  selectedAttribute: string,
  scopedEmails: Set<string>,
  focusedEmail: string,
  index: number,
  total: number,
): RelationshipGraphNode | null {
  const email = personEmail(person)
  if (!email) return null

  const angle = total <= 1 ? 0 : (Math.PI * 2 * index) / total
  const radius = Math.max(40, Math.sqrt(Math.max(total, 1)) * 28)
  const attributeValue = getRelationshipAttributeValue(person, selectedAttribute)
  const powerRating = Number(person.powerRating || 0)
  const totalConnections = Number(person.total_count || 0)
  const strongConnections = Number(person.strong_count || 0)

  return {
    id: email,
    email,
    emailKey: personEmailKey(person),
    label: personName(person),
    shortLabel: initialsFor(person),
    teamName: person.teamName || '',
    teamColor: person.teamColor || DEFAULT_NODE_COLOR,
    profileImage: person.profileImage || '',
    powerRating,
    attributeValue,
    attributeColor: relationshipAttributeColor(attributeValue, selectedAttribute),
    isScoped: scopedEmails.has(email),
    isFocused: focusedEmail === email,
    size: Math.max(5, Math.min(16, 6 + Math.sqrt(totalConnections) + Math.min(strongConnections, 5) * 0.45)),
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    person,
  }
}

function edgeForFocusedNode(edges: RelationshipGraphEdge[], focusedEmail: string, nodeId: string): RelationshipGraphEdge | null {
  return edges.find(edge => {
    return (edge.source === focusedEmail && edge.target === nodeId) || (edge.target === focusedEmail && edge.source === nodeId)
  }) || null
}

function stableOffset(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i)
    hash |= 0
  }
  return ((Math.abs(hash) % 1000) / 1000) - 0.5
}

function graphDistance(x: number, y: number): number {
  return Math.max(1, Math.sqrt((x * x) + (y * y)))
}

function edgeKey(source: string, target: string): string {
  return source < target ? `${source}__${target}` : `${target}__${source}`
}

function applyEgoLayout(nodes: RelationshipGraphNode[], edges: RelationshipGraphEdge[], focusedEmail: string) {
  if (!focusedEmail || nodes.length === 0) return

  const focusedNode = nodes.find(node => node.email === focusedEmail || node.id === focusedEmail)
  if (!focusedNode) return

  focusedNode.x = 0
  focusedNode.y = 12
  focusedNode.size = Math.max(focusedNode.size, 13)

  const edgeLookup: Record<string, RelationshipGraphEdge> = {}
  edges.forEach(edge => {
    edgeLookup[edgeKey(edge.source, edge.target)] = edge
  })

  const neighborNodes = nodes
    .filter(node => node.id !== focusedNode.id)
    .map(node => ({
      node,
      edge: edgeForFocusedNode(edges, focusedNode.id, node.id),
    }))
    .sort((a, b) => {
      const aEdge = a.edge
      const bEdge = b.edge
      const strongDelta = Number(Boolean(bEdge && bEdge.isStrong)) - Number(Boolean(aEdge && aEdge.isStrong))
      if (strongDelta !== 0) return strongDelta
      const weightDelta = Number(bEdge && bEdge.weight || 0) - Number(aEdge && aEdge.weight || 0)
      if (weightDelta !== 0) return weightDelta
      const teamCompare = String(a.node.teamName || '').localeCompare(String(b.node.teamName || ''))
      if (teamCompare !== 0) return teamCompare
      return String(a.node.label || '').localeCompare(String(b.node.label || ''))
    })

  const connected = neighborNodes.filter(item => item.edge)
  const disconnected = neighborNodes.filter(item => !item.edge)
  const goldenAngle = Math.PI * (3 - Math.sqrt(5))

  connected.forEach((item, index) => {
    const edge = item.edge as RelationshipGraphEdge
    const strong = edge.isStrong || Number(edge.weight || 0) >= STRONG_CONNECTION_WEIGHT
    const teamBias = stableOffset(item.node.teamName || item.node.id) * 0.8
    const angle = -Math.PI / 2 + (index - ((connected.length - 1) / 2)) * goldenAngle * 0.56 + teamBias
    const radius = (strong ? 92 : 138) + stableOffset(item.node.id) * 22

    item.node.x = Math.cos(angle) * radius
    item.node.y = Math.sin(angle) * radius + 8
  })

  disconnected.forEach((item, index) => {
    const angle = Math.PI * 0.1 + (index * Math.PI * 0.37)
    const radius = 178 + Math.floor(index / 6) * 36
    item.node.x = Math.cos(angle) * radius
    item.node.y = Math.sin(angle) * radius + 34
  })

  const layoutNodes = neighborNodes.map(item => item.node)
  for (let iteration = 0; iteration < 90; iteration++) {
    const forces: Record<string, { x: number; y: number }> = {}
    layoutNodes.forEach(node => {
      forces[node.id] = { x: 0, y: 0 }
    })

    layoutNodes.forEach(node => {
      const directEdge = edgeForFocusedNode(edges, focusedNode.id, node.id)
      const strong = directEdge && (directEdge.isStrong || Number(directEdge.weight || 0) >= STRONG_CONNECTION_WEIGHT)
      const targetRadius = directEdge ? (strong ? 88 : 130) : 178
      const dx = node.x - focusedNode.x
      const dy = node.y - focusedNode.y
      const distance = graphDistance(dx, dy)
      const pull = (distance - targetRadius) * 0.024

      forces[node.id].x -= (dx / distance) * pull
      forces[node.id].y -= (dy / distance) * pull
    })

    for (let i = 0; i < layoutNodes.length; i++) {
      for (let j = i + 1; j < layoutNodes.length; j++) {
        const a = layoutNodes[i]
        const b = layoutNodes[j]
        const dx = b.x - a.x
        const dy = b.y - a.y
        const distance = graphDistance(dx, dy)
        const sameTeam = a.teamName && a.teamName === b.teamName
        const neighborEdge = edgeLookup[edgeKey(a.id, b.id)]
        const preferred = neighborEdge
          ? (neighborEdge.isStrong ? 78 : 100)
          : sameTeam ? 112 : 144
        const attraction = neighborEdge
          ? (distance - preferred) * 0.018
          : sameTeam ? (distance - preferred) * 0.004 : 0
        const repulsion = Math.max(0, (42 - distance) * 0.038)
        const force = attraction - repulsion
        const unitX = dx / distance
        const unitY = dy / distance

        forces[a.id].x += unitX * force
        forces[a.id].y += unitY * force
        forces[b.id].x -= unitX * force
        forces[b.id].y -= unitY * force
      }
    }

    layoutNodes.forEach(node => {
      const force = forces[node.id]
      node.x += Math.max(-5, Math.min(5, force.x))
      node.y += Math.max(-5, Math.min(5, force.y))
    })
  }
}

export function buildRelationshipGraphModel(options: BuildRelationshipGraphOptions): RelationshipGraphModel {
  const scopedRows = Array.isArray(options.scopedRows) ? options.scopedRows : []
  const allRows = Array.isArray(options.allRows) && options.allRows.length > 0 ? options.allRows : scopedRows
  const linkMode = normalizeLinkMode(options.linkMode)
  const selectedAttribute = options.selectedAttribute || 'happiness'
  const focusedEmail = normalizeEmail(options.focusedEmail)
  const layoutMode = normalizeLayoutMode(options.layoutMode)
  const maxEdges = Number(options.maxEdges || DEFAULT_MAX_EDGES)
  const includeConnectedOutsideScope = options.includeConnectedOutsideScope !== false
  const { byEmail } = buildPersonIndexes(allRows)
  const scopedEmails = new Set(scopedRows.map(personEmail).filter(Boolean))
  const nodeEmails = new Set(scopedEmails)
  const edgesById: Record<string, RelationshipGraphEdge> = {}

  scopedRows.forEach(row => {
    const source = personEmail(row)
    if (!source) return

    if (linkMode === 'line_of_management_only') {
      const target = normalizeEmail(row.boss)
      if (!target || source === target) return
      const boss = byEmail[target]
      if (includeConnectedOutsideScope && boss) nodeEmails.add(target)
      if (!byEmail[target] && !scopedEmails.has(target)) return
      pushUniqueEdge(edgesById, {
        id: `management__${target}__${source}`,
        source: target,
        target: source,
        weight: MANAGEMENT_CONNECTION_WEIGHT,
        type: 'management',
        direction: 'one_way',
        isStrong: false,
        isFocused: focusedEmail === source || focusedEmail === target,
      })
      return
    }

    const strongMembers = memberList(row, 'strongMembers', 'strongArrData')
    const totalMembers = linkMode === 'meaningful_interactions_only'
      ? strongMembers
      : memberList(row, 'totalMembers', 'totalArrData')
    const strongSet = new Set(strongMembers)

    totalMembers.forEach(target => {
      if (!target || target === source) return
      const targetPerson = byEmail[target]
      if (!targetPerson && !scopedEmails.has(target)) return
      const isStrong = strongSet.has(target)
      if (linkMode === 'meaningful_interactions_only' && !isStrong) return
      if (includeConnectedOutsideScope && targetPerson) nodeEmails.add(target)

      const id = undirectedEdgeId(source, target)
      pushUniqueEdge(edgesById, {
        id,
        source,
        target,
        weight: isStrong ? STRONG_CONNECTION_WEIGHT : STANDARD_CONNECTION_WEIGHT,
        type: 'relationship',
        direction: 'one_way',
        isStrong,
        isFocused: focusedEmail === source || focusedEmail === target,
      })
    })
  })

  const sortedEdges = Object.values(edgesById)
    .sort((a, b) => {
      if (a.isFocused !== b.isFocused) return a.isFocused ? -1 : 1
      if (a.weight !== b.weight) return b.weight - a.weight
      return a.id.localeCompare(b.id)
    })
  const visibleEdges = sortedEdges.slice(0, Math.max(maxEdges, 0))

  visibleEdges.forEach(edge => {
    if (byEmail[edge.source]) nodeEmails.add(edge.source)
    if (byEmail[edge.target]) nodeEmails.add(edge.target)
  })

  const sortedNodeEmails = Array.from(nodeEmails)
    .filter(email => byEmail[email])
    .sort((a, b) => {
      const nodeA = byEmail[a]
      const nodeB = byEmail[b]
      const scopedDelta = Number(scopedEmails.has(b)) - Number(scopedEmails.has(a))
      if (scopedDelta !== 0) return scopedDelta
      const teamCompare = String(nodeA.teamName || '').localeCompare(String(nodeB.teamName || ''))
      if (teamCompare !== 0) return teamCompare
      return personName(nodeA).localeCompare(personName(nodeB))
    })

  const nodes = sortedNodeEmails
    .map((email, index) => makeNode(byEmail[email], selectedAttribute, scopedEmails, focusedEmail, index, sortedNodeEmails.length))
    .filter(Boolean) as RelationshipGraphNode[]

  const nodeIdSet = new Set(nodes.map(node => node.id))
  const edges = visibleEdges.filter(edge => nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target))

  if (layoutMode === 'ego') {
    applyEgoLayout(nodes, edges, focusedEmail)
  }

  return {
    nodes,
    edges,
    totalNodeCount: sortedNodeEmails.length,
    totalEdgeCount: sortedEdges.length,
    strongestHiddenEdgeWeight: sortedEdges.length > edges.length ? sortedEdges[edges.length].weight : null,
  }
}

export function filterRelationshipGraphModel(model: RelationshipGraphModel, threshold: number, focusedEmail = ''): RelationshipGraphModel {
  const normalizedFocus = normalizeEmail(focusedEmail)
  const edges = model.edges.filter(edge => {
    if (normalizedFocus && (edge.source === normalizedFocus || edge.target === normalizedFocus)) return true
    return edge.weight >= threshold
  })
  const nodeIds = new Set<string>()
  model.nodes.forEach(node => {
    if (node.isScoped || node.email === normalizedFocus) nodeIds.add(node.id)
  })
  edges.forEach(edge => {
    nodeIds.add(edge.source)
    nodeIds.add(edge.target)
  })

  return {
    ...model,
    nodes: model.nodes.filter(node => nodeIds.has(node.id)),
    edges,
  }
}

export function getRelationshipPersonByEmail(rows: RelationshipPerson[], email: string): RelationshipPerson | null {
  const normalizedEmail = normalizeEmail(email)
  return rows.find(row => personEmail(row) === normalizedEmail) || null
}
